import React, { useEffect, useRef, useCallback, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Outlet } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { getSocket } from './utils/socket';
import api from './utils/api';
// Public pages
import Landing from './pages/public/Landing';
import Marketplace from './pages/public/Marketplace';
import Nearby from './pages/public/Nearby';
import Farmers from './pages/public/Farmers';
import About from './pages/public/About';
import Contact from './pages/public/Contact';
import FAQ from './pages/public/FAQ';
import TermsOfService from './pages/public/TermsOfService';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import Guides from './pages/public/Guides';
import GuideArticle from './pages/public/GuideArticle';
import SellerGuide from './pages/public/SellerGuide';
import FarmerVerificationPage from './pages/public/FarmerVerificationPage';
import FarmerSupport from './pages/public/FarmerSupport';
import ProductDetails from './pages/public/ProductDetails';
import UserProfile from './pages/public/UserProfile';
import MaintenancePage from './pages/public/MaintenancePage';

// Auth pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Consumer pages
import Cart from './pages/consumer/Cart';
import Checkout from './pages/consumer/Checkout';
import OrderTracking from './pages/consumer/OrderTracking';
import ConsumerProfile from './pages/consumer/ConsumerProfile';
import ConsumerDashboard from './pages/consumer/ConsumerDashboard';
import ConsumerPayments from './pages/consumer/ConsumerPayments';
import RatingsReviews from './pages/consumer/RatingsReviews';
import Messages from './pages/consumer/Messages';
import MyOrders from './pages/consumer/MyOrders';
import MyReports from './pages/consumer/MyReports';
import MyReviews from './pages/consumer/MyReviews';
import PaymentCallback from './pages/consumer/PaymentCallback';
import NotificationPage from './pages/consumer/Notifications';

// Farmer pages
import FarmerDashboard from './pages/farmer/FarmerDashboard';
import FarmerProductList from './pages/farmer/FarmerProductList';
import FarmerAddEditProduct from './pages/farmer/FarmerAddEditProduct';
import FarmerOrderManagement from './pages/farmer/FarmerOrderManagement';
import FarmerOrderTracking from './pages/farmer/FarmerOrderTracking';
import FarmerPublicProfile from './pages/farmer/FarmerPublicProfile';
import FarmerAlerts from './pages/farmer/FarmerAlerts';
import FarmerSalesInsights from './pages/farmer/FarmerSalesInsights';
import FarmerAnalytics from './pages/farmer/FarmerAnalytics';
import FarmerPayments from './pages/farmer/FarmerPayments';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUserManagement from './pages/admin/AdminUserManagement';
import AdminProductManagement from './pages/admin/AdminProductManagement';
import AdminOrderManagement from './pages/admin/AdminOrderManagement';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminPaymentManagement from './pages/admin/AdminPaymentManagement';
import AdminWithdrawalManagement from './pages/admin/AdminWithdrawalManagement';
import AdminReviewModeration from './pages/admin/AdminReviewModeration';
import AdminSystemReports from './pages/admin/AdminSystemReports';
import AdminOrderReports from './pages/admin/AdminOrderReports';
import AdminSettings from './pages/admin/AdminSettings';
import './App.css';
import PublicRoute from './components/PublicRoute';
import { PopupProvider } from './context/PopupContext';
import { MaintenanceProvider, useMaintenance } from './context/MaintenanceContext';

const MaintenanceGuard = () => {
  const { maintenance, checked } = useMaintenance();
  const location = useLocation();

  if (!checked) return null;

  const isAdminRoute = location.pathname.startsWith('/admin');
  const userStr = localStorage.getItem('user');
  let isAdmin = false;
  try { isAdmin = userStr && JSON.parse(userStr).role === 'admin'; } catch {}

  if (maintenance && !isAdminRoute && !isAdmin) {
    return <MaintenancePage />;
  }

  return <Outlet />;
};

function App() {
  const heartbeatRef = useRef(null);

  const sendHeartbeat = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) api.put('/users/heartbeat').catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      getSocket();
      sendHeartbeat();
      heartbeatRef.current = setInterval(sendHeartbeat, 60000);
    }
    return () => clearInterval(heartbeatRef.current);
  }, [sendHeartbeat]);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '527730472160-0up0itis0lbgddvdjtciptvh8c7b6nl6.apps.googleusercontent.com';

  return (
    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>}>
    <GoogleOAuthProvider clientId={googleClientId}>
    <PopupProvider>
    <Router>
    <MaintenanceProvider>
      <div className="App">
        <Routes>
          {/* Authentication Pages - always accessible so admin can log in */}
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
          </Route>

          {/* Admin routes - never blocked by maintenance */}
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<AdminUserManagement />} />
          <Route path="/admin/users/farmers" element={<AdminUserManagement />} />
          <Route path="/admin/users/consumers" element={<AdminUserManagement />} />
          <Route path="/admin/products" element={<AdminProductManagement />} />
          <Route path="/admin/products/edit/:id" element={<FarmerAddEditProduct />} />
          <Route path="/admin/orders" element={<AdminOrderManagement />} />
          <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
          <Route path="/admin/payments" element={<AdminPaymentManagement />} />
          <Route path="/admin/withdrawals" element={<AdminWithdrawalManagement />} />
          <Route path="/admin/reviews" element={<AdminReviewModeration />} />
          <Route path="/admin/reports" element={<AdminSystemReports />} />
          <Route path="/admin/order-reports" element={<AdminOrderReports />} />
          <Route path="/admin/settings" element={<AdminSettings />} />

          {/* All other routes - guarded by maintenance */}
          <Route element={<MaintenanceGuard />}>
            {/* Public Pages */}
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/guides/:slug" element={<GuideArticle />} />
            <Route path="/seller-guide" element={<SellerGuide />} />
            <Route path="/farmer-verification" element={<FarmerVerificationPage />} />
            <Route path="/farmer-support" element={<FarmerSupport />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Grocery Pages */}
            <Route path="/market" element={<Marketplace />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/nearby" element={<Nearby />} />
            <Route path="/farmers" element={<Farmers />} />
            <Route path="/products/:id" element={<ProductDetails />} />

            {/* Consumer Pages */}
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/payment/callback" element={<PaymentCallback />} />
            <Route path="/orders" element={<MyOrders />} />
            <Route path="/consumer/payments" element={<ConsumerPayments />} />
            <Route path="/reports" element={<MyReports />} />
            <Route path="/consumer/reviews" element={<MyReviews />} />
            <Route path="/order/:id" element={<OrderTracking />} />
            <Route path="/consumer/profile" element={<ConsumerProfile />} />
            <Route path="/consumer/dashboard" element={<ConsumerDashboard />} />
            <Route path="/reviews" element={<RatingsReviews />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/notifications" element={<NotificationPage />} />

            {/* Farmer Dashboard Routes */}
            <Route path="/farmer/dashboard" element={<FarmerDashboard />} />
            <Route path="/farmer/products" element={<FarmerProductList />} />
            <Route path="/farmer/products/add" element={<FarmerAddEditProduct />} />
            <Route path="/farmer/products/edit/:id" element={<FarmerAddEditProduct />} />
            <Route path="/farmer/orders" element={<FarmerOrderManagement />} />
            <Route path="/farmer/order/:id" element={<FarmerOrderTracking />} />
            <Route path="/farmer/profile" element={<FarmerPublicProfile />} />
            <Route path="/farmer/alerts" element={<FarmerAlerts />} />
            <Route path="/farmer/analytics" element={<FarmerAnalytics />} />
            <Route path="/farmer/payments" element={<FarmerPayments />} />
            <Route path="/farmer/insights" element={<FarmerSalesInsights />} />
            <Route path="/farmer/sales-insights" element={<FarmerSalesInsights />} />

            {/* Public User Profiles */}
            <Route path="/users/:username" element={<UserProfile />} />
            <Route path="/profile/:username" element={<UserProfile />} />
            <Route path="/farmers/:username" element={<FarmerPublicProfile />} />
          </Route>
        </Routes>
      </div>
    </MaintenanceProvider>
    </Router>
    </PopupProvider>
    </GoogleOAuthProvider>
    </Suspense>
  );
}

export default App;
